/**
 * CORE-W requirement 7 — configuration is consumed here, not interpreted here.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE DEFECT THIS EXISTS TO CLOSE
 * ═══════════════════════════════════════════════════════════════════════════
 * `TechnicianForm` built one payload for everything it could edit and sent it
 * with `.update(techData)`. Two of those fields are no longer CORE-W's to
 * assert:
 *
 *   · `proficiencies`          — projected by CORE-E from
 *                                `technician_category_authority`
 *   · `efficiency_percentage`  — the running EMA CORE-AN updates in place
 *
 * The form reads them when it opens and writes them back when the manager
 * saves. So a manager who opened the form at 9:00, went to lunch, and changed
 * a technician's BUFFER at 11:00 silently reset every proficiency and the
 * learned efficiency to the 9:00 values. Nothing warned anyone, because from
 * the database's point of view it was an ordinary authorised write.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE TWO ARE NOT THE SAME KIND OF DAMAGE
 * ═══════════════════════════════════════════════════════════════════════════
 * They look alike and they are not, which is why they are handled together but
 * reasoned about separately.
 *
 * **Proficiency is a projection.** The truth lives in
 * `technician_category_authority`; `technicians.proficiencies` is written from
 * it by `core_e_recompute_authority`. A stale overwrite is repaired the next
 * time that runs — but until it does, assignment is reading a number a manager
 * typed weeks ago, and if CORE-E has not yet opened an authority row for a
 * category, `core_e_ensure_authority_row` will later adopt that stale number as
 * the BASELINE PRIOR. The damage outlives the overwrite.
 *
 * **Efficiency is not a projection — the column IS the state.** CORE-AN's EMA
 * reads `COALESCE(t.efficiency_percentage, 100)` as its previous value and
 * writes the new one back, alongside `completed_jobs_count` as the sample size.
 * There is no authority table to re-project from. A stale overwrite does not
 * get repaired; it silently rewinds the learning and every subsequent update
 * compounds from the wrong number.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT A MANAGER MAY STILL ASSERT
 * ═══════════════════════════════════════════════════════════════════════════
 * A manager's opening estimate remains constitutionally valid and is NOT
 * removed here. CORE-E says so in its own words — `baseline_score` is "a
 * manager's opening estimate — an assertion, never truth" — and CORE-AN seeds
 * its EMA from whatever value the technician starts with.
 *
 * The distinction is WHEN, not WHETHER:
 *
 *   · CREATING a technician — the manager states an opening estimate. It is
 *     carried, because that is exactly how the prior enters: CORE-E's
 *     `core_e_ensure_authority_row` reads `technicians.proficiencies` to seed
 *     `baseline_score`, and CORE-AN's EMA starts from the stored efficiency.
 *   · EDITING an existing technician — there is no permitted path. CORE-E
 *     exposes no manager-facing baseline RPC; every one of its functions is
 *     `REVOKE ALL … FROM PUBLIC, anon, authenticated` and granted to
 *     `service_role` alone, and its table admits writes from the service role
 *     only. So the generic edit does not carry them, and the controls that
 *     offered them stop pretending to.
 *
 * No second authority is created here. This module asserts nothing; it only
 * declines to.
 */

/**
 * The columns an ordinary CORE-W technician edit must never assert.
 *
 * The three `efficiency_source*` columns are CORE-G's, and they are here for a
 * reason worth stating: **provenance travels with the value it describes.**
 * CORE-G stamps `efficiency_source: 'manual'` with a null sample size whenever
 * a person types a number, which is exactly right — and its trigger stamps
 * `'learned'` with the real sample size when CORE-AN's ledger writes one.
 *
 * Dropping the number but keeping the stamp would be worse than dropping
 * neither: the row would hold CORE-AN's learned 137% while claiming a human
 * typed it from no evidence at all. A reader trying to tell a measured value
 * from an asserted one — the entire point of CORE-G — would be told the
 * opposite of the truth. So all four move together, or none do.
 */
export const DERIVED_TECHNICIAN_COLUMNS = [
  'proficiencies',
  'efficiency_percentage',
  'efficiency_source',
  'efficiency_sample_size',
  'efficiency_source_at',
] as const;

export type DerivedTechnicianColumn = (typeof DERIVED_TECHNICIAN_COLUMNS)[number];

/**
 * LEGACY_TECHNICIAN_ROLE_DOCTRINE — one role system, and it is not this column.
 *
 *   `membership_work_roles`  = operational role authority (edited in Team
 *                              Management, shown read-only on the editors).
 *   `technicians.role`       = TEMPORARY legacy compatibility field only.
 *
 * Apprentice / Technician / Specialist / Foreman is no longer a manager-facing
 * concept. No technician editor, and no onboarding step, presents it as a
 * choice. The column survives only because it is NOT NULL (with a CHECK over
 * those four words and no DEFAULT) and a few display surfaces still print it.
 * Nothing reads it for assignment, eligibility, ranking, permission or pay.
 *
 * So:
 *   · an EDIT never writes it — the stored value, whatever history put there,
 *     is left exactly as it is;
 *   · a CREATE receives its compatibility value from the server
 *     (`create-technician-with-profile`, `_shared/shopWorkerIdentity.ts`),
 *     never from a person.
 */
export const LEGACY_COMPATIBILITY_TECHNICIAN_COLUMNS = ['role'] as const;

export type LegacyCompatibilityTechnicianColumn =
  (typeof LEGACY_COMPATIBILITY_TECHNICIAN_COLUMNS)[number];

/**
 * The compatibility value for `technicians.role` on a row created directly by
 * the client (the only such path is TechnicianEditor's rarely-reached create
 * branch). It is the existing convention every server create path already
 * writes, and the CHECK admits it. It is not a choice anybody makes and it
 * confers nothing.
 */
export const LEGACY_TECHNICIAN_ROLE_COMPAT = 'Technician' as const;

/**
 * The payload for editing an EXISTING technician.
 *
 * Everything CORE-W genuinely owns — name, hours, breaks, buffer, employment
 * status, specialties, skill tier, preferences — passes through untouched. The
 * derived columns are dropped, so whatever CORE-E and CORE-AN have most
 * recently written survives the save regardless of how long the form sat open;
 * and the legacy `role` is dropped, so an edit can never author it.
 */
export function buildTechnicianConfigurationUpdate<T extends Record<string, unknown>>(
  full: T,
): Omit<T, DerivedTechnicianColumn | LegacyCompatibilityTechnicianColumn> {
  const next = { ...full } as Record<string, unknown>;
  for (const column of DERIVED_TECHNICIAN_COLUMNS) delete next[column];
  for (const column of LEGACY_COMPATIBILITY_TECHNICIAN_COLUMNS) delete next[column];
  return next as Omit<T, DerivedTechnicianColumn | LegacyCompatibilityTechnicianColumn>;
}

/**
 * The payload for CREATING a technician.
 *
 * Deliberately the identity function, and deliberately a named one. The
 * opening estimate belongs here and nowhere else, and giving that its own
 * name means the creation path cannot later be "tidied" onto the edit builder
 * without someone noticing that they mean different things.
 */
export function buildTechnicianCreationPayload<T extends Record<string, unknown>>(
  full: T,
): T {
  return { ...full };
}
